import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Package, Eye, Edit, Trash2, Boxes, CircleDollarSign, AlertTriangle, CheckCircle2, FolderTree, Truck,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import {
  PageHeader, StatsCard, StatsGrid, AppButton, ConfirmModal, SearchInput,
  StatusBadge, Paginator, EmptyState,
  FilterToolbar, FilterButton, ActiveFilterChips, FilterPanel, FilterSelectField,
  useListFilters,
} from "../ui";
import {
  ASSET_STATUS_BADGE, ASSET_STATUS_LABEL, ASSET_STATUSES, formatCurrency,
} from "./assetConstants";
import "./assets.scss";

const PAGE_SIZE = 20;

const AssetsListPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // The FilterPanel pattern (D3). `empty: ""` on BOTH keys is load-bearing: this
  // page's API reads the empty string as "no filter", not the "all" sentinel the
  // /sa/* pages use, and useListFilters defaults to "all". Getting it wrong shows
  // every filter as permanently active and nothing throws (Phase 6.3).
  const filters = useListFilters(
    {
      status: {
        label: "Status",
        empty: "",
        options: [
          { value: "", label: "All Statuses" },
          ...ASSET_STATUSES.map((st) => ({ value: st, label: ASSET_STATUS_LABEL[st] })),
        ],
      },
      category: {
        label: "Category",
        empty: "",
        options: [
          { value: "", label: "All Categories" },
          ...categories.map((c) => ({ value: c._id, label: c.name })),
        ],
      },
    },
    () => setPage(1),
  );
  const { status: statusFilter, category: categoryFilter } = filters.values;

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { page, pageSize: PAGE_SIZE };
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.categoryId = categoryFilter;
      const { data } = await apiClient.get("/assets", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      setToast({ show: true, message: "Failed to load assets.", type: "error" });
    }
    setLoading(false);
  };

  const fetchSummary = async () => {
    try {
      const { data } = await apiClient.get("/assets/summary");
      setSummary(data);
    } catch { /* non-blocking */ }
  };

  const fetchCategories = async () => {
    try {
      const { data } = await apiClient.get("/assets/categories");
      setCategories(data.categories || []);
    } catch { /* non-blocking */ }
  };

  useEffect(() => { fetchCategories(); fetchSummary(); }, []);
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [page, statusFilter, categoryFilter]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); fetchData(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiClient.delete(`/assets/${deleteTarget._id}`);
      setToast({ show: true, message: "Asset removed.", type: "success" });
      setDeleteTarget(null);
      fetchData(); fetchSummary();
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || "Failed to remove asset.", type: "error" });
      setDeleteTarget(null);
    }
  };

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  return (
    <div className="assets-page">
      <CommonToaster show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />

      <PageHeader
        icon={Package}
        title="Assets"
        description="Manage institute inventory — laptops, equipment, furniture and more"
        actions={
          <>
            <AppButton variant="secondary" onClick={() => navigate("/assets/categories")}>
              <FolderTree size={16} /> Categories
            </AppButton>
            <AppButton variant="secondary" onClick={() => navigate("/assets/vendors")}>
              <Truck size={16} /> Vendors
            </AppButton>
            <AppButton onClick={() => navigate("/assets/new")}>
              <Plus size={16} /> Add Asset
            </AppButton>
          </>
        }
      />

      {summary && (
        <StatsGrid cols={6} className="assets-page__stats">
          <StatsCard title="Total Assets" value={summary.total || 0} icon={Boxes} iconColor="blue" />
          <StatsCard title="In Stock" value={summary.byStatus?.['in-stock'] || 0} icon={CheckCircle2} iconColor="emerald" />
          <StatsCard title="Assigned" value={summary.byStatus?.['assigned'] || 0} icon={Package} iconColor="violet" />
          <StatsCard title="Under Maintenance" value={summary.byStatus?.['under-maintenance'] || 0} icon={AlertTriangle} iconColor="amber" />
          <StatsCard title="Total Cost" value={formatCurrency(summary.totalCost)} icon={CircleDollarSign} iconColor="blue" />
          <StatsCard title="Book Value" value={formatCurrency(summary.totalBookValue)} icon={CircleDollarSign} iconColor="emerald" />
        </StatsGrid>
      )}

      <FilterToolbar
        search={
          <SearchInput
            value={search}
            onChange={(v) => setSearch(v)}
            placeholder="Search by tag, name, brand, serial..."
          />
        }
        filters={<FilterButton count={filters.count} onClick={filters.open} />}
      />
      <ActiveFilterChips items={filters.activeFilters} onClearAll={filters.reset} />

      <FilterPanel {...filters.panelProps} title="Filter Assets">
        <FilterSelectField {...filters.selectProps("status")} />
        <FilterSelectField {...filters.selectProps("category")} />
      </FilterPanel>

      <div className="assets-page__table-card">
        {loading ? (
          <div className="assets-page__empty">Loading...</div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Package size={24} />}
            title="No assets yet"
            description="Add your first asset to start tracking."
            action={<AppButton onClick={() => navigate("/assets/new")}><Plus size={16} /> Add Asset</AppButton>}
          />
        ) : (
          <table className="assets-table">
            <thead>
              <tr>
                <th>Tag</th>
                <th>Name</th>
                <th>Category</th>
                <th>Status</th>
                <th>Assignee</th>
                <th>Cost</th>
                <th>Book Value</th>
                <th className="assets-table__col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(a => (
                <tr key={a._id}>
                  <td><span className="assets-table__tag">{a.assetTag}</span></td>
                  <td>
                    <div className="assets-table__name">{a.name}</div>
                    {(a.brand || a.model) && (
                      <div className="assets-table__sub">
                        {[a.brand, a.model].filter(Boolean).join(" • ")}
                      </div>
                    )}
                  </td>
                  <td>{a.categoryId?.name || "—"}</td>
                  <td>
                    <StatusBadge status={ASSET_STATUS_BADGE[a.status] || "active"}>
                      {ASSET_STATUS_LABEL[a.status] || a.status}
                    </StatusBadge>
                  </td>
                  <td>{a.currentAssignment?.assigneeName || "—"}</td>
                  <td>{formatCurrency(a.purchaseCost, a.currency)}</td>
                  <td>{formatCurrency(a.currentBookValue, a.currency)}</td>
                  <td className="assets-table__col-actions">
                    <AppButton size="sm" variant="ghost" onClick={() => navigate(`/assets/${a._id}`)} aria-label="View">
                      <Eye size={14} />
                    </AppButton>
                    <AppButton size="sm" variant="ghost" onClick={() => navigate(`/assets/${a._id}/edit`)} aria-label="Edit">
                      <Edit size={14} />
                    </AppButton>
                    <AppButton size="sm" variant="ghost" onClick={() => setDeleteTarget(a)} aria-label="Delete">
                      <Trash2 size={14} />
                    </AppButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <Paginator page={page} totalPages={totalPages} onChange={setPage} />
      )}

      <ConfirmModal
        show={!!deleteTarget}
        title="Remove Asset?"
        message={deleteTarget ? `"${deleteTarget.name}" (${deleteTarget.assetTag}) will be marked as retired and hidden from active lists.` : ""}
        confirmText="Remove"
        variant="danger"
        onConfirm={handleDelete}
        onHide={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default AssetsListPage;
